// components/tier-branch/CreateEditTierModal.tsx
// "Create New Tier" / "Edit Tier" dialog — configures a transaction band
// with deposit/withdraw limits. Mirrors the modal seen in the Tier Overview.
import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Box, FormControlLabel, Checkbox, Switch, Typography, CircularProgress,
} from "@mui/material";
import * as api from "../../services/tierBranchApi";
import type { Tier, TierInput } from "../../types/tierBranch";
import type { Notify } from "./useSnack";

const EMPTY: TierInput = {
  name: "",
  minTransactionAmount: 0,
  maxTransactionAmount: 0,
  maxTransactions: 0,
  minDeposit: 0,
  maxDeposit: 0,
  minWithdraw: 0,
  maxWithdraw: 0,
  allowDeposit: true,
  allowWithdraw: true,
  isManual: false,
  manualPaymentEnabled: true,
  isActive: true,
  priority: 0,
};

const num = (v: string) => (v === "" ? 0 : Number(v));

const CreateEditTierModal: React.FC<{
  open: boolean;
  tier: Tier | null; // null = create mode
  onClose: () => void;
  onSaved: () => void;
  notify: Notify;
}> = ({ open, tier, onClose, onSaved, notify }) => {
  const [form, setForm] = useState<TierInput>(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (tier) {
      const { id, userCount, createdAt, updatedAt, ...rest } = tier;
      setForm(rest);
    } else {
      setForm(EMPTY);
    }
  }, [open, tier]);

  const set = <K extends keyof TierInput>(key: K, value: TierInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      notify("Tier name is required", "warning");
      return;
    }
    if (form.maxTransactionAmount < form.minTransactionAmount) {
      notify("Max transaction amount must be ≥ min", "warning");
      return;
    }
    setSaving(true);
    try {
      if (tier) {
        await api.updateTier(tier.id, form);
        notify("Tier updated", "success");
      } else {
        await api.createTier(form);
        notify("Tier created", "success");
      }
      onSaved();
      onClose();
    } catch (e: any) {
      notify(e?.message || "Failed to save tier", "error");
    } finally {
      setSaving(false);
    }
  };

  const numField = (label: string, key: keyof TierInput) => (
    <TextField
      label={label}
      type="number"
      size="small"
      fullWidth
      value={(form[key] as number) ?? 0}
      onChange={(e) => set(key, num(e.target.value) as any)}
    />
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{tier ? "Edit Tier" : "Create New Tier"}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Tier Name"
            size="small"
            fullWidth
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
          />

          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Qualifying range (auto-assignment)
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            {numField("Min Transaction Amount", "minTransactionAmount")}
            {numField("Max Transaction Amount", "maxTransactionAmount")}
            {numField("Max Transactions", "maxTransactions")}
            {numField("Priority (lower first)", "priority")}
          </Box>

          <Box sx={{ display: "flex", gap: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.allowDeposit}
                  onChange={(e) => set("allowDeposit", e.target.checked)}
                />
              }
              label="Deposit"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.allowWithdraw}
                  onChange={(e) => set("allowWithdraw", e.target.checked)}
                />
              }
              label="Withdraw"
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            {numField("Min Deposit", "minDeposit")}
            {numField("Max Deposit", "maxDeposit")}
            {numField("Min Withdraw", "minWithdraw")}
            {numField("Max Withdraw", "maxWithdraw")}
          </Box>

          <Box sx={{ display: "flex", gap: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isManual}
                  onChange={(e) => set("isManual", e.target.checked)}
                />
              }
              label="Manual (skip auto-assign)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.manualPaymentEnabled}
                  onChange={(e) => set("manualPaymentEnabled", e.target.checked)}
                />
              }
              label="Manual deposit / withdraw (bank, UPI)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => set("isActive", e.target.checked)}
                />
              }
              label="Active"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {tier ? "Save Changes" : "Create Tier"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditTierModal;
